import { useMemo, useState } from "react";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { AppHeader } from "@/components/app/AppHeader";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { formatIst } from "@/lib/tz";

export const Route = createFileRoute("/_app/_admin/admin/moderator-audit")({
  component: () => <Navigate to="/admin/settings" search={{ tab: "moderator-audit" } as never} />,
});

type RecentItem = {
  submission_table: string;
  submission_id: string;
  submission_owner_id: string | null;
  owner_name: string | null;
  note: string | null;
  edited_at: string;
};

type Row = {
  moderator_id: string;
  moderator_name: string | null;
  department: string | null;
  total_corrections: number;
  last_correction_at: string;
  recent: RecentItem[] | null;
};

export function ModeratorAuditPage() {
  const [search, setSearch] = useState("");

  const { data: rows = [], isLoading } = useQuery<Row[]>({
    queryKey: ["moderator-corrections"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_moderator_corrections", {});
      if (error) throw error;
      return (data ?? []) as Row[];
    },
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.moderator_name ?? "").toLowerCase().includes(q) ||
        (r.department ?? "").toLowerCase().includes(q),
    );
  }, [rows, search]);

  return (
    <>
      <AppHeader subtitle="Every correction issued by a moderator, grouped by who issued it" />
      <div className="px-[22px] pb-6">
        <div className="rounded-lg border border-border bg-card shadow-sm">
          <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
                Moderator audit
              </div>
              <h2 className="text-[16px] font-bold">{rows.length} moderators</h2>
            </div>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or department…"
              className="h-9 w-64 text-[12px]"
            />
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Moderator</TableHead>
                <TableHead>Department</TableHead>
                <TableHead className="text-right">Corrections</TableHead>
                <TableHead>Last action</TableHead>
                <TableHead>Recent items</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={5} className="h-20 text-center text-[12px] text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="h-20 text-center text-[12px] text-muted-foreground">
                    No corrections recorded yet.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((r) => (
                <TableRow key={r.moderator_id}>
                  <TableCell className="text-[12px] font-medium">
                    {r.moderator_name ?? "—"}
                  </TableCell>
                  <TableCell className="text-[12px]">
                    <Badge variant="secondary" className="text-[10px]">
                      {r.department ?? "—"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right text-[12px] font-semibold">
                    {r.total_corrections}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-[12px] text-muted-foreground">
                    {r.last_correction_at
                      ? formatIst(r.last_correction_at, "EEE, dd MMM HH:mm")
                      : "—"}
                  </TableCell>
                  <TableCell className="max-w-[420px]">
                    {r.recent && r.recent.length > 0 ? (
                      <Accordion type="single" collapsible>
                        <AccordionItem value="recent" className="border-none">
                          <AccordionTrigger className="py-1 text-[11px] hover:no-underline">
                            Show last {r.recent.length}
                          </AccordionTrigger>
                          <AccordionContent>
                            <ul className="space-y-1.5">
                              {r.recent.map((it) => (
                                <li
                                  key={`${it.submission_table}-${it.submission_id}-${it.edited_at}`}
                                  className="rounded bg-muted/50 px-2 py-1.5 text-[11px]"
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="font-medium">
                                      {it.owner_name ?? "Unknown"}
                                    </span>
                                    <span className="text-muted-foreground">
                                      {formatIst(it.edited_at, "dd MMM HH:mm")}
                                    </span>
                                  </div>
                                  <div className="mt-0.5 text-muted-foreground">
                                    <code className="text-[10px]">
                                      {it.submission_table}
                                    </code>
                                    {it.note ? ` · ${it.note}` : ""}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    ) : (
                      <span className="text-[11px] text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  );
}
